/* eslint-disable react/no-this-in-sfc */
import getXOrLastGroupNode from "components/charts/modern/helpers/getXOrLastGroupNode";
import type { ChartOverlay, FlagSeriesData } from "components/charts/modern/overlay/types";
import { OverlayDataKind } from "components/charts/modern/overlay/types";
import { isEarningOverlayKind } from "components/charts/modern/overlay/useChartOverlays";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import classNames from "components/ui/classNames";
import { isSameDay } from "date-fns";
import type { TooltipFormatterContextObject } from "highcharts";
import type { SeriesAreaOptions } from "highcharts/highstock";
import type { ReactElement } from "react";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage } from "react-intl";

interface QuotesChartTooltipProps {
  locale: string;
  context: TooltipFormatterContextObject;
  intl: IntlShape;
}
export default function QuotesChartTooltip({ locale, context, intl }: QuotesChartTooltipProps): ReactElement {
  const percentageFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 1,
    });
  }, []);

  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: locale === "USA" ? "USD" : "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    minimumSignificantDigits: 2,
  });

  if (context.x && typeof context.x !== "string" && context.series.type === "flags") {
    if (!context.series.options.custom || !context.series.options.custom.overlay || !(context.point as any).custom) {
      return <span />;
    }
    const overlay = context.series.options.custom.overlay as ChartOverlay;
    const pointData = (context.point as any).custom as FlagSeriesData;
    if (!pointData) {
      return <span />;
    }

    return (
      <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
        {isEarningOverlayKind(overlay.kind) && pointData.value && (
          <>
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
              {getXOrLastGroupNode(context, intl)}
            </span>

            <div className="flex items-center justify-between space-x-3">
              <span
                className={classNames("rounded-md px-2 py-1 font-mono text-xs font-bold", {
                  "bg-green-50 text-emerald-700": pointData.label === "Net Income",
                  "bg-blue-50 text-blue-700": pointData.label === "FFO",
                  "bg-red-50 text-rose-700": pointData.value < 0,
                })}
              >
                {pointData.label}
              </span>

              <span
                className={classNames("font-mono font-semibold", {
                  "text-rose-700": pointData.value < 0,
                })}
              >
                {formatFinancialValue(pointData.value)}
              </span>
            </div>
          </>
        )}

        {overlay?.kind === OverlayDataKind.MarketCrisis && pointData && (
          <>
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
              {pointData.time || intl.formatDate(context.x, { year: "numeric" })}
            </span>
            <div className="max-w-xs truncate px-1 font-sans font-semibold leading-6 text-slate-700">
              {pointData.label}
            </div>
          </>
        )}
      </div>
    );
  }
  if (context.x && typeof context.x !== "string" && context.points && context.points.length > 0) {
    const overlay = (
      context.series.options.custom ? context.series.options.custom.overlay : null
    ) as ChartOverlay | null;
    let flagSeriesData: FlagSeriesData | null = null;
    if (overlay && overlay.data && Array.isArray(overlay.data)) {
      flagSeriesData =
        overlay.data.find((candidate) => {
          return context.x && typeof context.x !== "string" && isSameDay(candidate.x, context.x);
        }) || null;
    }
    return (
      <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
        <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
          {getXOrLastGroupNode(context, intl)}
        </span>

        <div className="space-y-1">
          {context.points
            .sort((a, b) => {
              return (b.y || 0) - (a.y || 0);
            })
            .map(({ y, series }) => {
              if (!y) {
                return null;
              }

              const color = String((series.userOptions as SeriesAreaOptions).color);
              const lineColor = String((series.userOptions as SeriesAreaOptions).lineColor);

              const lastY = series.points.length > 0 ? series.points[series.points.length - 1].y : null;
              const returnSoFar = lastY ? lastY / y - 1 : null;

              return (
                <div key={color} className="flex items-center justify-between space-x-3">
                  <span
                    className="rounded-md px-2 py-1  font-mono text-xs font-bold"
                    style={{
                      backgroundColor: color,
                      color: lineColor,
                    }}
                  >
                    {series.name}
                  </span>

                  <span className="font-mono font-semibold">{y ? moneyFormatter.format(y) : ""}</span>

                  {returnSoFar !== null && (
                    <span
                      className={classNames("font-mono font-semibold", {
                        "text-rose-600": returnSoFar < 0,
                        "text-slate-600": returnSoFar === 0,
                        "text-emerald-600": returnSoFar > 0,
                      })}
                    >
                      {percentageFormatter.format(returnSoFar)}
                    </span>
                  )}
                </div>
              );
            })}

          {overlay && flagSeriesData && isEarningOverlayKind(overlay.kind) && flagSeriesData.value && (
            <div className="flex items-center justify-between space-x-3">
              <span
                className={classNames("rounded-md px-2 py-1 font-mono text-xs font-bold", {
                  "bg-green-50 text-emerald-700": flagSeriesData.label === "Net Income",
                  "bg-blue-50 text-blue-700": flagSeriesData.label === "FFO",
                  "bg-red-50 text-rose-700": flagSeriesData.value < 0,
                })}
              >
                {flagSeriesData.label}
              </span>

              <span
                className={classNames("font-mono font-semibold", {
                  "text-rose-700": flagSeriesData.value < 0,
                })}
              >
                {formatFinancialValue(flagSeriesData.value)}
              </span>
            </div>
          )}

          {overlay?.kind === OverlayDataKind.MarketCrisis && flagSeriesData && (
            <div className="flex items-center justify-between space-x-3">
              <span className={classNames("rounded-md px-2 py-1 font-mono text-xs font-bold")}>
                <FormattedMessage defaultMessage="Crise" id="ZSRfmp" description="Tooltip do gráfico de cotação" />
              </span>
              <span className={classNames("font-mono font-semibold")}>{flagSeriesData.label}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <span />;
}
